import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import RichTextEditor from '../components/RichTextEditor';
import {
  ArrowLeft, Type, Upload, Palette, Save, Plus, Trash2,
  Image as ImageIcon, Loader2, X, GripVertical, RotateCcw,
  Copy, ClipboardPaste, Files
} from 'lucide-react';
import { uploadImage as firebaseUpload } from '../firebase/config';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FONTS = [
  // Sans-Serif — Modern & Clean
  'Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Raleway',
  'Nunito', 'Nunito Sans', 'Quicksand', 'DM Sans', 'Cabin', 'Barlow',
  'Mulish', 'Karla', 'Rubik', 'Work Sans', 'Exo 2', 'Josefin Sans',
  'Maven Pro', 'Overpass', 'PT Sans', 'Source Sans 3', 'Space Grotesk',
  'Syne', 'Ubuntu', 'Varela Round', 'Noto Sans', 'Dosis', 'Prompt', 'Rajdhani',
  // Serif — Elegant & Premium
  'Playfair Display', 'Merriweather', 'Lora', 'EB Garamond', 'Cormorant Garamond',
  'Cinzel', 'Spectral', 'Vollkorn', 'PT Serif', 'Noto Serif', 'Arvo',
  'Bitter', 'Bree Serif', 'DM Serif Display', 'Marcellus', 'Prata',
  'Philosopher', 'Italiana', 'Yeseva One', 'Zilla Slab',
  // Display & Bold
  'Bebas Neue', 'Oswald', 'Anton', 'Fjalla One', 'Teko', 'Alfa Slab One',
  'Abril Fatface', 'Archivo Black', 'Titan One', 'Righteous', 'Play',
  // Script & Handwritten
  'Dancing Script', 'Pacifico', 'Lobster', 'Great Vibes', 'Sacramento',
  'Satisfy', 'Kaushan Script', 'Caveat', 'Shadows Into Light', 'Permanent Marker',
  'Comfortaa',
  // Monospace
  'Inconsolata', 'Source Code Pro',
  // System Fallbacks
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana',
];

const FONT_SIZE_PRESETS = [5, 6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 96, 120, 144];

const LOGO_SHAPES = [
  { value: 'circle', label: 'Round' },
  { value: 'square', label: 'Square' },
  { value: 'rounded-rect', label: 'Rounded Rect' },
];

const getLogoShapeStyle = (shape) => {
  switch (shape) {
    case 'square': return { borderRadius: '12%' };
    case 'rounded-rect': return { borderRadius: '16%', aspectRatio: '1.4/1' };
    case 'circle': default: return { borderRadius: '50%' };
  }
};

const SNAP_THRESHOLD = 1.5; // percentage points for snapping

const COLORS = [
  '#000000', '#FFFFFF', '#1F2937', '#374151', '#6B7280',
  '#EF4444', '#F97316', '#F59E0B', '#22C55E', '#3B82F6',
  '#6366F1', '#8B5CF6', '#A855F7', '#EC4899', '#D4AF37'
];

const BG_PRESETS = [
  { label: 'Black', color: '#000000' },
  { label: 'White', color: '#FFFFFF' },
  { label: 'Navy', color: '#1e293b' },
  { label: 'Dark Green', color: '#14532d' },
  { label: 'Burgundy', color: '#7f1d1d' },
  { label: 'Gold', color: '#78350f' },
  { label: 'Charcoal', color: '#27272a' },
  { label: 'Midnight', color: '#0f172a' },
];

const DEFAULT_FRONT_ELEMENTS = [
  { id: 'name', text: 'Your Name', x: 50, y: 38, fontSize: 26, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'bold', textAlign: 'center', movable: false },
  { id: 'title', text: 'Job Title', x: 50, y: 52, fontSize: 14, fontFamily: 'Inter', color: '#D4AF37', fontWeight: 'normal', textAlign: 'center', movable: false },
  { id: 'company', text: 'Company Name', x: 50, y: 65, fontSize: 12, fontFamily: 'Inter', color: '#94a3b8', fontWeight: 'normal', textAlign: 'center', movable: false },
];

const DEFAULT_BACK_ELEMENTS = [
  { id: 'phone', text: '+91 98765 43210', x: 50, y: 35, fontSize: 12, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'normal', textAlign: 'center', movable: false },
  { id: 'email', text: 'name@company.com', x: 50, y: 48, fontSize: 11, fontFamily: 'Inter', color: '#94a3b8', fontWeight: 'normal', textAlign: 'center', movable: false },
  { id: 'website', text: 'www.company.com', x: 50, y: 60, fontSize: 11, fontFamily: 'Inter', color: '#D4AF37', fontWeight: 'normal', textAlign: 'center', movable: false },
  { id: 'address', text: 'City, Country', x: 50, y: 73, fontSize: 10, fontFamily: 'Inter', color: '#6b7280', fontWeight: 'normal', textAlign: 'center', movable: false },
];

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

// Helper function to normalize text elements - ensure textAlign defaults to 'center'
const normalizeTextElements = (elements) => {
  if (!elements || !Array.isArray(elements)) return [];
  return elements.map(el => ({
    ...el,
    textAlign: el.textAlign || 'center' // Default to center alignment
  }));
};

const AdminTemplateBuilder = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const bgImageRef = useRef(null);
  const backBgImageRef = useRef(null);
  const logoImageRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const canvasWrapperRef = useRef(null);

  const isEditing = Boolean(templateId);

  // Check admin access
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  
  useEffect(() => {
    if (user && !isAdmin) {
      toast({ title: "Access Denied", description: "Only admin can access this page", variant: "destructive" });
      navigate('/');
    }
  }, [user, isAdmin, navigate, toast]);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // CSS transform-based scaling: render at 500px ref width, scale down to fit
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCanvasScale(entry.contentRect.width / 500);
      }
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [loadingTemplate]);

  // Template metadata
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateMrp, setTemplateMrp] = useState('999');
  const [templatePrice, setTemplatePrice] = useState('899');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [thumbnailImage, setThumbnailImage] = useState(null); // Custom thumbnail for category page
  const thumbnailInputRef = useRef(null);
  // Active side: 'front' or 'back'
  const [activeSide, setActiveSide] = useState('front');

  // Front Canvas state
  const [frontBgColor, setFrontBgColor] = useState('#1e293b');
  const [frontBgImage, setFrontBgImage] = useState(null);
  const [frontTextElements, setFrontTextElements] = useState([...DEFAULT_FRONT_ELEMENTS]);
  const [frontLogoPosition, setFrontLogoPosition] = useState({ x: 50, y: 15, size: 18 }); // null = removed

  // Back Canvas state
  const [backBgColor, setBackBgColor] = useState('#1e293b');
  const [backBgImage, setBackBgImage] = useState(null);
  const [backTextElements, setBackTextElements] = useState([...DEFAULT_BACK_ELEMENTS]);
  const [backLogoPosition, setBackLogoPosition] = useState({ x: 50, y: 15, size: 14 }); // null = removed
  // QR placeholder — null means not placed; admin must explicitly add it
  const [backQrPosition, setBackQrPosition] = useState(null);

  // UI state
  const [selectedElement, setSelectedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState(null);
  const [activePanel, setActivePanel] = useState('background');
  const [alignGuides, setAlignGuides] = useState([]);
  const [editingTextId, setEditingTextId] = useState(null);
  
  // Clipboard state for copy-paste
  const [copiedElement, setCopiedElement] = useState(null);
  
  // Swipe detection state
  const swipeStartRef = useRef({ x: 0, y: 0, time: 0 });

  // Swipe handlers for switching front/back
  const handleSwipeStart = useCallback((e) => {
    if (isDragging) return;
    const touch = e.touches?.[0];
    if (touch) {
      swipeStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    }
  }, [isDragging]);

  const handleSwipeEnd = useCallback((e) => {
    if (isDragging) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const deltaTime = Date.now() - swipeStartRef.current.time;
    
    // Only count as swipe if: horizontal movement > 50px, vertical < 100px, time < 500ms
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 100 && deltaTime < 500) {
      if (deltaX < 0 && activeSide === 'front') {
        // Swipe left -> show back
        setActiveSide('back');
        setSelectedElement(null);
        setEditingTextId(null);
      } else if (deltaX > 0 && activeSide === 'back') {
        // Swipe right -> show front
        setActiveSide('front');
        setSelectedElement(null);
        setEditingTextId(null);
      }
    }
  }, [isDragging, activeSide]);

  // Getters for active side
  const bgColor = activeSide === 'front' ? frontBgColor : backBgColor;
  const setBgColor = activeSide === 'front' ? setFrontBgColor : setBackBgColor;
  const bgImage = activeSide === 'front' ? frontBgImage : backBgImage;
  const setBgImage = activeSide === 'front' ? setFrontBgImage : setBackBgImage;
  const textElements = activeSide === 'front' ? frontTextElements : backTextElements;
  const setTextElements = activeSide === 'front' ? setFrontTextElements : setBackTextElements;
  const logoPosition = activeSide === 'front' ? frontLogoPosition : backLogoPosition;
  const setLogoPosition = activeSide === 'front' ? setFrontLogoPosition : setBackLogoPosition;

  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, subcatRes] = await Promise.all([
          fetch(`${API_URL}/api/card-designer/categories?active_only=false&include_empty=true`),
          fetch(`${API_URL}/api/card-designer/subcategories?active_only=false`)
        ]);
        if (catRes.ok) setCategories(await catRes.json());
        if (subcatRes.ok) setSubcategories(await subcatRes.json());
      } catch (err) { console.error('Error loading data', err); }
    };
    loadData();
  }, []);

  // Load existing template when editing
  useEffect(() => {
    if (!templateId) return;
    const loadTemplate = async () => {
      setLoadingTemplate(true);
      try {
        const res = await fetch(`${API_URL}/api/card-designer/templates/${templateId}`);
        if (!res.ok) throw new Error('Template not found');
        const t = await res.json();
        setTemplateName(t.name || '');
        setTemplateDescription(t.description || '');
        setTemplateMrp(String(t.mrp || t.price || '999'));
        setTemplatePrice(String(t.price || '899'));
        setCategoryId(t.category_id || '');
        setSubcategoryId(t.subcategory_id || '');
        // Restore front design (new fields first, fallback to old seeded fields) - with center alignment default
        setFrontTextElements(normalizeTextElements(t.front_text_elements?.length ? t.front_text_elements : (t.default_text_elements || DEFAULT_FRONT_ELEMENTS)));
        setFrontBgColor(t.front_bg_color || t.background_color || '#1e293b');
        if (t.front_bg_image) setFrontBgImage(t.front_bg_image);
        setFrontLogoPosition(t.front_logo_position !== undefined ? t.front_logo_position : (t.logo_placeholder || { x: 50, y: 15, size: 18 }));
        // Restore back design - with center alignment default
        setBackTextElements(normalizeTextElements(t.back_text_elements?.length ? t.back_text_elements : DEFAULT_BACK_ELEMENTS));
        setBackBgColor(t.back_bg_color || '#1e293b');
        if (t.back_bg_image) setBackBgImage(t.back_bg_image);
        setBackLogoPosition(t.back_logo_position !== undefined ? t.back_logo_position : { x: 50, y: 15, size: 14 });
        setBackQrPosition(t.back_qr_position || null);
        // Load thumbnail if exists
        if (t.thumbnail_image) setThumbnailImage(t.thumbnail_image);
      } catch (err) {
        toast({ title: 'Error', description: 'Failed to load template', variant: 'destructive' });
        navigate('/admin/card-designer');
      } finally { setLoadingTemplate(false); }
    };
    loadTemplate();
  }, [templateId, navigate, toast]);

  const filteredSubcats = subcategories.filter(s => s.category_id === categoryId);

  // ========== DRAG HANDLING ==========
  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: ((clientX - rect.left) / rect.width) * 100, y: ((clientY - rect.top) / rect.height) * 100 };
  }, []);

  const handleCanvasMouseDown = useCallback((e, elementId, type = 'text') => {
    e.preventDefault();
    e.stopPropagation();
    // Clear any editing state when starting a drag
    setEditingTextId(null);
    setIsDragging(true);
    setDragTarget({ id: elementId, type });
    if (type === 'text') setSelectedElement(elementId);
  }, []);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isDragging || !dragTarget) return;
    // Prevent default to stop scroll/touch interference
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let cx = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    let cy = Math.max(5, Math.min(95, ((clientY - rect.top) / rect.height) * 100));
    
    // Simplified snap guide positions (static guides only - much faster)
    const staticGuides = [
      { x: 50, y: 50 },   // center
      { x: 10, y: 10 },   // margins
      { x: 90, y: 90 },
      { x: 25, y: 25 },   // quarters
      { x: 75, y: 75 },
    ];

    const guides = [];
    for (const pos of staticGuides) {
      if (Math.abs(cx - pos.x) < SNAP_THRESHOLD) {
        cx = pos.x;
        guides.push({ type: 'vertical', pos: pos.x });
        break; // Only snap to one vertical guide
      }
    }
    for (const pos of staticGuides) {
      if (Math.abs(cy - pos.y) < SNAP_THRESHOLD) {
        cy = pos.y;
        guides.push({ type: 'horizontal', pos: pos.y });
        break; // Only snap to one horizontal guide
      }
    }
    setAlignGuides(guides);

    if (dragTarget.type === 'text') {
      if (activeSide === 'front') {
        setFrontTextElements(prev => prev.map(el => el.id === dragTarget.id ? { ...el, x: cx, y: cy } : el));
      } else {
        setBackTextElements(prev => prev.map(el => el.id === dragTarget.id ? { ...el, x: cx, y: cy } : el));
      }
    } else if (dragTarget.type === 'logo') {
      if (activeSide === 'front') {
        setFrontLogoPosition(prev => ({ ...prev, x: cx, y: cy }));
      } else {
        setBackLogoPosition(prev => ({ ...prev, x: cx, y: cy }));
      }
    } else if (dragTarget.type === 'qr') {
      setBackQrPosition(prev => prev ? { ...prev, x: cx, y: cy } : prev);
    }
  }, [isDragging, dragTarget, activeSide]);

  const handleCanvasMouseUp = useCallback((e) => {
    if (e) e.preventDefault();
    setIsDragging(false);
    setDragTarget(null);
    setAlignGuides([]);
  }, []);

  // ========== TEXT ELEMENT MANAGEMENT ==========
  const addTextElement = () => {
    const newId = `text-${Date.now()}`;
    setTextElements(prev => [...prev, { id: newId, text: 'New Text', x: 50, y: 80, fontSize: 12, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'normal', textAlign: 'center', movable: false }]);
    setSelectedElement(newId);
  };

  const updateTextElement = (id, field, value) => {
    setTextElements(prev => prev.map(el => el.id === id ? { ...el, [field]: value } : el));
  };

  const deleteTextElement = (id) => {
    setTextElements(prev => prev.filter(el => el.id !== id));
    if (selectedElement === id) setSelectedElement(null);
  };

  // Copy text element to clipboard (local state + localStorage for cross-template)
  const copyTextElement = (id) => {
    const element = textElements.find(el => el.id === id);
    if (element) {
      const copiedData = { ...element, copiedAt: Date.now() };
      setCopiedElement(copiedData);
      // Store in localStorage for cross-template paste
      localStorage.setItem('copiedTextElement', JSON.stringify(copiedData));
      toast({ title: "Copied!", description: `Text "${element.text}" copied to clipboard` });
    }
  };

  // Paste text element from clipboard
  const pasteTextElement = () => {
    // Try local state first, then localStorage
    let elementToPaste = copiedElement;
    if (!elementToPaste) {
      const stored = localStorage.getItem('copiedTextElement');
      if (stored) {
        elementToPaste = JSON.parse(stored);
      }
    }
    
    if (elementToPaste) {
      const newId = `text-${Date.now()}`;
      const newElement = {
        ...elementToPaste,
        id: newId,
        // Offset position slightly so it's visible as a new element
        x: Math.min(95, (elementToPaste.x || 50) + 5),
        y: Math.min(95, (elementToPaste.y || 50) + 5),
      };
      delete newElement.copiedAt; // Remove metadata
      setTextElements(prev => [...prev, newElement]);
      setSelectedElement(newId);
      toast({ title: "Pasted!", description: `Text "${newElement.text}" added to canvas` });
    } else {
      toast({ title: "Nothing to paste", description: "Copy a text element first", variant: "destructive" });
    }
  };

  // Duplicate text element (copy + paste in one action)
  const duplicateTextElement = (id) => {
    const element = textElements.find(el => el.id === id);
    if (element) {
      const newId = `text-${Date.now()}`;
      const newElement = {
        ...element,
        id: newId,
        x: Math.min(95, element.x + 5),
        y: Math.min(95, element.y + 5),
      };
      setTextElements(prev => [...prev, newElement]);
      setSelectedElement(newId);
      toast({ title: "Duplicated!", description: `Text "${element.text}" duplicated` });
    }
  };

  // Keyboard shortcuts for copy-paste
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedElement) {
        e.preventDefault();
        copyTextElement(selectedElement);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        pasteTextElement();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElement) {
        e.preventDefault();
        duplicateTextElement(selectedElement);
      }
      if (e.key === 'Delete' && selectedElement) {
        e.preventDefault();
        deleteTextElement(selectedElement);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, textElements, copiedElement]);

  // Load copied element from localStorage on mount
  React.useEffect(() => {
    const stored = localStorage.getItem('copiedTextElement');
    if (stored) {
      try {
        setCopiedElement(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse copied element from localStorage');
      }
    }
  }, []);

  const handleBgImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBgImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const setLogo = activeSide === 'front' ? setFrontLogoPosition : setBackLogoPosition;
      setLogo(prev => prev ? { ...prev, image: ev.target.result } : prev);
    };
    reader.readAsDataURL(file);
  };

  // Reset current side to defaults
  const handleReset = () => {
    if (activeSide === 'front') {
      setFrontBgColor('#1e293b'); setFrontBgImage(null);
      setFrontTextElements([...DEFAULT_FRONT_ELEMENTS]);
      setFrontLogoPosition({ x: 50, y: 15, size: 18 });
    } else {
      setBackBgColor('#1e293b'); setBackBgImage(null);
      setBackTextElements([...DEFAULT_BACK_ELEMENTS]);
      setBackLogoPosition({ x: 50, y: 15, size: 14 });
      setBackQrPosition(null);
    }
    setSelectedElement(null);
  };
  // ========== RENDER & SAVE ==========
  const renderSide = (sideBgColor, sideBgImage, sideTextElements, sideLogo, showLogo = true) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1013; canvas.height = 638; // 3.375:2.125 credit card ratio

      const drawText = () => {
        sideTextElements.forEach(el => {
          const x = (el.x / 100) * canvas.width;
          const y = (el.y / 100) * canvas.height;
          const sz = el.fontSize * (canvas.width / 243); // 243pt = 3.375in × 72pt/in
          ctx.font = `${el.fontWeight === 'bold' ? 'bold ' : ''}${sz}px ${el.fontFamily}`;
          ctx.fillStyle = el.color; ctx.textAlign = el.textAlign || 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(el.text, x, y);
        });
      };

      const drawLogoPlaceholder = (lx, ly, lSize, shape) => {
        ctx.strokeStyle = 'rgba(212, 175, 55, 0.5)'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
        ctx.beginPath();
        if (shape === 'circle') {
          ctx.arc(lx, ly, lSize / 2, 0, Math.PI * 2);
        } else if (shape === 'square') {
          const r = lSize * 0.12;
          ctx.moveTo(lx - lSize/2 + r, ly - lSize/2);
          ctx.arcTo(lx + lSize/2, ly - lSize/2, lx + lSize/2, ly + lSize/2, r);
          ctx.arcTo(lx + lSize/2, ly + lSize/2, lx - lSize/2, ly + lSize/2, r);
          ctx.arcTo(lx - lSize/2, ly + lSize/2, lx - lSize/2, ly - lSize/2, r);
          ctx.arcTo(lx - lSize/2, ly - lSize/2, lx + lSize/2, ly - lSize/2, r);
        } else {
          const w = lSize * 1.4; const h = lSize; const r = lSize * 0.16;
          ctx.moveTo(lx - w/2 + r, ly - h/2);
          ctx.arcTo(lx + w/2, ly - h/2, lx + w/2, ly + h/2, r);
          ctx.arcTo(lx + w/2, ly + h/2, lx - w/2, ly + h/2, r);
          ctx.arcTo(lx - w/2, ly + h/2, lx - w/2, ly - h/2, r);
          ctx.arcTo(lx - w/2, ly - h/2, lx + w/2, ly - h/2, r);
        }
        ctx.stroke(); ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(212, 175, 55, 0.3)'; ctx.font = `${lSize * 0.2}px Inter`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('LOGO', lx, ly);
      };

      const drawLogo = () => {
        if (!showLogo || !sideLogo) return Promise.resolve();
        const lx = (sideLogo.x / 100) * canvas.width;
        const ly = (sideLogo.y / 100) * canvas.height;
        const lSize = (sideLogo.size / 100) * canvas.width * 0.5;
        const shape = sideLogo.shape || 'circle';

        if (sideLogo.image) {
          return new Promise((res) => {
            const logoImg = new Image(); logoImg.crossOrigin = 'anonymous';
            logoImg.onload = () => {
              ctx.save();
              ctx.beginPath();
              if (shape === 'circle') {
                ctx.arc(lx, ly, lSize / 2, 0, Math.PI * 2);
              } else if (shape === 'square') {
                const r = lSize * 0.12;
                ctx.moveTo(lx - lSize/2 + r, ly - lSize/2);
                ctx.arcTo(lx + lSize/2, ly - lSize/2, lx + lSize/2, ly + lSize/2, r);
                ctx.arcTo(lx + lSize/2, ly + lSize/2, lx - lSize/2, ly + lSize/2, r);
                ctx.arcTo(lx - lSize/2, ly + lSize/2, lx - lSize/2, ly - lSize/2, r);
                ctx.arcTo(lx - lSize/2, ly - lSize/2, lx + lSize/2, ly - lSize/2, r);
              } else {
                const w = lSize * 1.4; const h = lSize; const r = lSize * 0.16;
                ctx.moveTo(lx - w/2 + r, ly - h/2);
                ctx.arcTo(lx + w/2, ly - h/2, lx + w/2, ly + h/2, r);
                ctx.arcTo(lx + w/2, ly + h/2, lx - w/2, ly + h/2, r);
                ctx.arcTo(lx - w/2, ly + h/2, lx - w/2, ly - h/2, r);
                ctx.arcTo(lx - w/2, ly - h/2, lx + w/2, ly - h/2, r);
              }
              ctx.clip();
              const dw = shape === 'rounded-rect' ? lSize * 1.4 : lSize;
              const dh = lSize;
              ctx.drawImage(logoImg, lx - dw/2, ly - dh/2, dw, dh);
              ctx.restore();
              res();
            };
            logoImg.onerror = () => { drawLogoPlaceholder(lx, ly, lSize, shape); res(); };
            logoImg.src = sideLogo.image;
          });
        } else {
          drawLogoPlaceholder(lx, ly, lSize, shape);
          return Promise.resolve();
        }
      };

      const finalize = async () => {
        ctx.fillStyle = sideBgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        drawText();
        await drawLogo();
        resolve(canvas.toDataURL('image/png'));
      };

      if (sideBgImage) {
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); finalize(); };
        img.onerror = () => finalize();
        img.src = sideBgImage;
      } else { finalize(); }
    });
  };

  const uploadImage = async (dataUrl, filename, token) => {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const uniqueName = `${Date.now()}-${filename}`;
      const storagePath = `template-images/${uniqueName}`;
      const firebaseUrl = await firebaseUpload(blob, storagePath);
      return firebaseUrl;
    } catch (err) {
      console.error('Firebase upload failed, falling back to server:', err);
      // Fallback to server upload if Firebase fails
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append('file', blob, filename);
      const res = await fetch(`${API_URL}/api/card-designer/admin/templates/upload-image`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) { const data = await res.json(); return `${API_URL}${data.url}`; }
      return dataUrl;
    }
  };

  const handleSave = async () => {
    if (!templateName || !templatePrice || !categoryId) {
      toast({ title: "Missing fields", description: "Name, price, and category are required", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const token = await user.getIdToken();

      // Upload background images to server if they are base64
      let persistentFrontBgImage = frontBgImage;
      let persistentBackBgImage = backBgImage;
      if (frontBgImage && frontBgImage.startsWith('data:')) {
        persistentFrontBgImage = await uploadImage(frontBgImage, 'bg-front.png', token);
      }
      if (backBgImage && backBgImage.startsWith('data:')) {
        persistentBackBgImage = await uploadImage(backBgImage, 'bg-back.png', token);
      }

      // Upload logo images to server if they are base64
      let persistentFrontLogo = frontLogoPosition;
      let persistentBackLogo = backLogoPosition;
      if (frontLogoPosition?.image && frontLogoPosition.image.startsWith('data:')) {
        const uploadedUrl = await uploadImage(frontLogoPosition.image, 'logo-front.png', token);
        persistentFrontLogo = { ...frontLogoPosition, image: uploadedUrl };
      }
      if (backLogoPosition?.image && backLogoPosition.image.startsWith('data:')) {
        const uploadedUrl = await uploadImage(backLogoPosition.image, 'logo-back.png', token);
        persistentBackLogo = { ...backLogoPosition, image: uploadedUrl };
      }

      // Generate flattened preview thumbnails using original state (base64 works reliably in canvas)
      const frontPreview = await renderSide(frontBgColor, frontBgImage, frontTextElements, frontLogoPosition, true);
      const backPreview = await renderSide(backBgColor, backBgImage, backTextElements, backLogoPosition, false);
      const frontImageUrl = await uploadImage(frontPreview, 'template-front.png', token);
      const backImageUrl = await uploadImage(backPreview, 'template-back.png', token);

      // Upload thumbnail if exists and is base64
      let persistentThumbnail = thumbnailImage;
      if (thumbnailImage && thumbnailImage.startsWith('data:')) {
        persistentThumbnail = await uploadImage(thumbnailImage, 'template-thumbnail.png', token);
      }

      const payload = {
        name: templateName,
        category_id: categoryId,
        subcategory_id: subcategoryId || null,
        mrp: parseFloat(templateMrp),
        price: parseFloat(templatePrice),
        description: templateDescription,
        front_image: frontImageUrl,
        back_image: backImageUrl,
        thumbnail_image: persistentThumbnail,
        is_active: true,
        front_text_elements: frontTextElements,
        front_bg_color: frontBgColor,
        front_bg_image: persistentFrontBgImage,
        front_logo_position: persistentFrontLogo,
        back_text_elements: backTextElements,
        back_bg_color: backBgColor,
        back_bg_image: persistentBackBgImage,
        back_logo_position: persistentBackLogo,
        back_qr_position: backQrPosition,
      };

      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing
        ? `${API_URL}/api/card-designer/admin/templates/${templateId}`
        : `${API_URL}/api/card-designer/admin/templates`;

      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({ title: isEditing ? "Template Updated" : "Template Created", description: isEditing ? "Template saved successfully" : "Base template saved with front and back designs" });
        navigate('/admin/card-designer');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const selectedEl = textElements.find(el => el.id === selectedElement);

  if (loadingTemplate) {
    return (
      <Layout><AdminNav />
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <AdminNav />
      <div className="bg-gray-50 flex flex-col" style={{ minHeight: 'calc(100vh - 48px)' }}>
        {/* Top bar */}
        <div className="bg-white border-b px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between z-30 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/card-designer')} data-testid="back-to-designer">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="h-5 w-px bg-gray-300 hidden sm:block" />
            <h1 className="text-sm sm:text-lg font-semibold text-gray-800 truncate">{isEditing ? 'Edit Template' : 'New Template'}</h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm" size="sm" data-testid="save-template-builder-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}<span className="hidden sm:inline">{isEditing ? 'Save Changes' : 'Save Template'}</span><span className="sm:hidden">Save</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-auto lg:overflow-hidden">
          {/* LEFT: Editor Tools */}
          <div className="lg:w-80 flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r overflow-y-auto order-2 lg:order-1" data-testid="editor-tools-panel">
            <div className="p-3 space-y-3">
              {/* Panel Tabs */}
              <div className="flex gap-1 bg-gray-50 rounded-lg p-1">
                {[
                  { id: 'background', icon: Palette, label: 'BG' },
                  { id: 'text', icon: Type, label: 'Text' },
                  { id: 'logo', icon: ImageIcon, label: 'Logo' },
                  { id: 'qr', icon: null, label: 'QR' },
                ].map(p => (
                  <Button key={p.id} variant={activePanel === p.id ? 'default' : 'ghost'} size="sm"
                    className={`flex-1 text-xs ${activePanel === p.id ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                    onClick={() => setActivePanel(p.id)} data-testid={`panel-${p.id}`}>
                    {p.icon ? <p.icon className="w-3.5 h-3.5 mr-1" /> : <span className="mr-1 text-xs">▦</span>}{p.label}
                  </Button>
                ))}
              </div>

            {/* Background Panel */}
            {activePanel === 'background' && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 text-sm">Background ({activeSide === 'front' ? 'Front' : 'Back'})</h3>
                <div>
                  <Label className="text-xs mb-2 block">Color Presets</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {BG_PRESETS.map(p => (
                      <button key={p.color} title={p.label}
                        className={`h-10 rounded-md border-2 transition-all ${bgColor === p.color ? 'border-amber-500 scale-105 shadow-md' : 'border-gray-200 hover:border-gray-400'}`}
                        style={{ backgroundColor: p.color }} onClick={() => setBgColor(p.color)} />
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Custom Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
                    <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="flex-1 font-mono text-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Background Image</Label>
                  <input ref={activeSide === 'front' ? bgImageRef : backBgImageRef} type="file" accept="image/*" className="hidden" onChange={handleBgImageUpload} />
                  {bgImage ? (
                    <div className="mt-2 relative">
                      <img src={bgImage} alt="BG" className="w-full h-20 object-cover rounded border" />
                      <Button size="sm" variant="destructive" className="absolute top-1 right-1 h-6 w-6 p-0" onClick={() => setBgImage(null)}><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <Button variant="outline" className="w-full mt-1 border-dashed h-12 text-xs" onClick={() => (activeSide === 'front' ? bgImageRef : backBgImageRef).current?.click()}>
                      <Upload className="w-3.5 h-3.5 mr-1" />Upload BG Image
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Text Elements Panel */}
            {activePanel === 'text' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 text-sm">Text ({activeSide === 'front' ? 'Front' : 'Back'})</h3>
                  <Button size="sm" variant="outline" onClick={addTextElement} className="h-7 text-xs" data-testid="add-text-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Add
                  </Button>
                  <Button size="sm" variant="outline" onClick={pasteTextElement} className="h-7 text-xs" title="Paste (Ctrl+V)">
                    <ClipboardPaste className="w-3.5 h-3.5 mr-1" />Paste
                  </Button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {textElements.map(el => (
                    <div key={el.id} className={`flex items-center gap-1.5 p-1.5 rounded text-xs cursor-pointer transition-colors ${selectedElement === el.id ? 'bg-amber-50 border border-amber-300' : 'bg-gray-50 border border-transparent hover:bg-gray-100'}`}
                      onClick={() => setSelectedElement(el.id)} data-testid={`text-item-${el.id}`}>
                      <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate flex-1">{el.text}</span>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-blue-500 flex-shrink-0" onClick={(ev) => { ev.stopPropagation(); copyTextElement(el.id); }} title="Copy (Ctrl+C)">
                        <Copy className="w-2.5 h-2.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-green-500 flex-shrink-0" onClick={(ev) => { ev.stopPropagation(); duplicateTextElement(el.id); }} title="Duplicate (Ctrl+D)">
                        <Files className="w-2.5 h-2.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-500 flex-shrink-0" onClick={(ev) => { ev.stopPropagation(); deleteTextElement(el.id); }} title="Delete">
                        <Trash2 className="w-2.5 h-2.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                {/* Keyboard shortcuts hint */}
                <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-0.5"><kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px]">Ctrl+C</kbd> Copy</span>
                  <span className="flex items-center gap-0.5"><kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px]">Ctrl+V</kbd> Paste</span>
                  <span className="flex items-center gap-0.5"><kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px]">Ctrl+D</kbd> Duplicate</span>
                  <span className="flex items-center gap-0.5"><kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px]">Del</kbd> Delete</span>
                </div>
                {copiedElement && (
                  <div className="text-[10px] text-blue-600 mt-1 flex items-center gap-1">
                    <Copy className="w-3 h-3" />
                    <span>Clipboard: "{copiedElement.text?.substring(0, 20)}{copiedElement.text?.length > 20 ? '...' : ''}"</span>
                  </div>
                )}
                {selectedEl && (
                  <div className="space-y-2 pt-2 border-t">
                    <div>
                      <Label className="text-xs">Text</Label>
                      <Input value={selectedEl.text} onChange={(e) => updateTextElement(selectedEl.id, 'text', e.target.value)} className="text-sm" data-testid="text-content-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Font</Label>
                      <Select value={selectedEl.fontFamily} onValueChange={(v) => updateTextElement(selectedEl.id, 'fontFamily', v)}>
                        <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">{FONTS.map(f => <SelectItem key={f} value={f} className="text-xs" style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Size</Label>
                      <div className="flex items-center gap-1 mt-0.5">
                        <button className="h-8 w-7 flex items-center justify-center rounded border text-gray-500 hover:bg-gray-100 text-sm font-bold" onClick={() => updateTextElement(selectedEl.id, 'fontSize', Math.max(5, selectedEl.fontSize - 1))}>−</button>
                        <input
                          type="number" min={5} max={144}
                          value={selectedEl.fontSize}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            if (!isNaN(v)) updateTextElement(selectedEl.id, 'fontSize', Math.min(144, Math.max(5, v)));
                          }}
                          className="h-8 w-14 text-center text-xs border rounded outline-none focus:border-amber-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid="font-size-input"
                        />
                        <button className="h-8 w-7 flex items-center justify-center rounded border text-gray-500 hover:bg-gray-100 text-sm font-bold" onClick={() => updateTextElement(selectedEl.id, 'fontSize', Math.min(144, selectedEl.fontSize + 1))}>+</button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {[12, 18, 24, 36, 48, 72, 96, 144].map(s => (
                          <button key={s} onClick={() => updateTextElement(selectedEl.id, 'fontSize', s)}
                            className={`px-1.5 py-0.5 rounded text-[10px] border transition-all ${selectedEl.fontSize === s ? 'bg-amber-100 border-amber-400 text-amber-700 font-semibold' : 'border-gray-200 hover:border-gray-400 text-gray-500'}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Weight</Label>
                        <Select value={selectedEl.fontWeight} onValueChange={(v) => updateTextElement(selectedEl.id, 'fontWeight', v)}>
                          <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="bold">Bold</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Align</Label>
                        <Select value={selectedEl.textAlign} onValueChange={(v) => updateTextElement(selectedEl.id, 'textAlign', v)}>
                          <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Color</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {COLORS.map(c => (
                          <button key={c} className={`w-6 h-6 rounded-full border-2 transition-all ${selectedEl.color === c ? 'border-amber-500 scale-110' : 'border-gray-200'}`}
                            style={{ backgroundColor: c }} onClick={() => updateTextElement(selectedEl.id, 'color', c)} />
                        ))}
                        <input type="color" value={selectedEl.color} onChange={(e) => updateTextElement(selectedEl.id, 'color', e.target.value)} className="w-5 h-5 rounded-full cursor-pointer" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div>
                        <Label className="text-xs">User Can Move</Label>
                        <p className="text-[10px] text-gray-400">Allow users to reposition</p>
                      </div>
                      <button
                        onClick={() => updateTextElement(selectedEl.id, 'movable', !selectedEl.movable)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${selectedEl.movable ? 'bg-amber-500' : 'bg-gray-300'}`}
                        data-testid="toggle-movable"
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selectedEl.movable ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Logo Panel */}
            {activePanel === 'logo' && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 text-sm">Logo ({activeSide === 'front' ? 'Front' : 'Back'})</h3>
                {logoPosition ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 font-medium">Logo placed</span>
                      <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={() => setLogoPosition(null)} data-testid="remove-logo-btn">Remove</Button>
                    </div>
                    <div>
                      <Label className="text-xs">Logo Image</Label>
                      <input ref={logoImageRef} type="file" accept="image/*" className="hidden" onChange={handleLogoImageUpload} />
                      {logoPosition.image ? (
                        <div className="mt-1.5 relative inline-block">
                          <img src={logoPosition.image} alt="Logo" className="w-16 h-16 object-cover border rounded-lg" />
                          <button className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow" onClick={() => setLogoPosition(prev => ({ ...prev, image: null }))}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <Button variant="outline" className="w-full mt-1 border-dashed h-9 text-xs" onClick={() => logoImageRef.current?.click()}>
                          <Upload className="w-3.5 h-3.5 mr-1" />Upload Logo
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Size ({logoPosition.size}%)</Label>
                      <Slider value={[logoPosition.size]} onValueChange={([v]) => setLogoPosition(prev => ({ ...prev, size: v }))} min={5} max={40} step={1} className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs">Shape</Label>
                      <div className="flex gap-1.5 mt-1.5">
                        {LOGO_SHAPES.map(s => (
                          <button key={s.value}
                            className={`flex-1 px-2 py-1.5 text-xs rounded border-2 transition-all ${(logoPosition.shape || 'circle') === s.value ? 'border-amber-500 bg-amber-50 font-semibold' : 'border-gray-200 hover:border-gray-400'}`}
                            onClick={() => setLogoPosition(prev => ({ ...prev, shape: s.value }))}
                            data-testid={`logo-shape-${s.value}`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 italic">Drag on canvas to reposition.</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Add a logo to the card.</p>
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-xs h-9" onClick={() => setLogoPosition(activeSide === 'front' ? { x: 50, y: 15, size: 18, shape: 'circle' } : { x: 50, y: 15, size: 14, shape: 'circle' })} data-testid="add-logo-btn">
                      <ImageIcon className="w-3.5 h-3.5 mr-1" /> Add Logo
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* QR Code Panel */}
            {activePanel === 'qr' && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 text-sm">QR Code (Back)</h3>
                <p className="text-xs text-gray-500">Add a QR code placeholder to the back.</p>
                {activeSide !== 'back' && (
                  <p className="text-xs text-amber-600 font-medium">Switch to Back Side to add/manage QR placement.</p>
                )}
                {activeSide === 'back' && (
                  <>
                    {backQrPosition ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-green-600 font-medium">✓ QR placed on back</span>
                          <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => setBackQrPosition(null)}>Remove</Button>
                        </div>
                        <div>
                          <Label className="text-xs">Size ({backQrPosition.size}%)</Label>
                          <Slider value={[backQrPosition.size]} onValueChange={([v]) => setBackQrPosition(prev => ({ ...prev, size: v }))} min={10} max={50} step={1} className="mt-2" />
                        </div>
                        <div>
                          <Label className="text-xs">QR Color</Label>
                          <div className="flex items-center gap-2 mt-1.5">
                            <input
                              type="color"
                              value={backQrPosition.color || '#ffffff'}
                              onChange={(e) => setBackQrPosition(prev => ({ ...prev, color: e.target.value }))}
                              className="w-9 h-9 rounded border border-gray-200 cursor-pointer p-0.5 bg-white"
                              data-testid="qr-color-picker"
                            />
                            <div className="flex gap-1.5 flex-wrap">
                              {['#ffffff','#000000','#f59e0b','#1e293b','#10b981','#3b82f6'].map(c => (
                                <button key={c} title={c}
                                  className={`w-6 h-6 rounded border-2 transition-all ${backQrPosition.color === c ? 'border-amber-500 scale-110' : 'border-gray-200 hover:border-amber-300'}`}
                                  style={{ backgroundColor: c }}
                                  onClick={() => setBackQrPosition(prev => ({ ...prev, color: c }))}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 italic">Drag the QR box on the canvas to reposition.</p>
                      </div>
                    ) : (
                      <Button className="w-full bg-amber-600 hover:bg-amber-700 text-xs" onClick={() => setBackQrPosition({ x: 50, y: 60, size: 28 })} data-testid="add-qr-btn">
                        <span className="mr-1">▦</span> Add QR Code to Back
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </div>

          {/* CENTER: Canvas */}
          <div className="flex-1 flex items-start justify-center pt-3 px-3 sm:px-4 overflow-y-auto order-1 lg:order-2">
            <div className="w-full lg:max-w-2xl">
              {/* Side Toggle */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="flex bg-white rounded-lg p-0.5 shadow-sm border">
                  <button onClick={() => { setActiveSide('front'); setSelectedElement(null); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeSide === 'front' ? 'bg-amber-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    data-testid="side-front-btn">Front</button>
                  <button onClick={() => { setActiveSide('back'); setSelectedElement(null); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeSide === 'back' ? 'bg-amber-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    data-testid="side-back-btn">Back</button>
                </div>
                <Button variant="ghost" size="sm" onClick={handleReset} title="Reset this side" className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Canvas scaling wrapper: renders at 500px ref width, CSS-transforms down */}
              <div 
                ref={canvasWrapperRef} 
                className="w-full" 
                style={{ aspectRatio: '3.375 / 2.125', overflow: 'hidden' }}
                onTouchStart={handleSwipeStart}
                onTouchEnd={handleSwipeEnd}
              >
              <div ref={canvasRef}
                className="relative select-none overflow-hidden rounded-xl shadow-lg"
                style={{
                  width: '500px', height: `${Math.round(500 / 3.375 * 2.125)}px`,
                  transform: `scale(${canvasScale})`,
                  transformOrigin: 'top left',
                  backgroundColor: bgColor,
                  backgroundImage: bgImage ? `url(${bgImage})` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  cursor: isDragging ? 'grabbing' : 'default',
                  touchAction: 'pan-y pinch-zoom',
                }}
                onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp} onTouchMove={handleCanvasMouseMove}
                onTouchEnd={handleCanvasMouseUp} data-testid="template-canvas"
                onMouseDown={(e) => {
                  // Only deselect when clicking directly on canvas background
                  if (e.target === canvasRef.current) {
                    e.preventDefault();
                    setActivePanel('background');
                    setSelectedElement(null);
                    setEditingTextId(null);
                  }
                }}
              >
                {/* Alignment Guide Lines - Show when dragging */}
                {isDragging && (
                  <>
                    {/* Vertical center line */}
                    <div 
                      className="absolute top-0 bottom-0 w-px bg-amber-500 pointer-events-none z-30"
                      style={{ 
                        left: '50%', 
                        opacity: 0.6,
                        boxShadow: '0 0 4px rgba(245, 158, 11, 0.8)'
                      }} 
                    />
                    {/* Horizontal center line */}
                    <div 
                      className="absolute left-0 right-0 h-px bg-amber-500 pointer-events-none z-30"
                      style={{ 
                        top: '50%', 
                        opacity: 0.6,
                        boxShadow: '0 0 4px rgba(245, 158, 11, 0.8)'
                      }} 
                    />
                    {/* Current element position lines */}
                    {dragTarget?.type === 'text' && selectedElement && (() => {
                      const el = textElements.find(t => t.id === selectedElement);
                      if (!el) return null;
                      return (
                        <>
                          <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                            style={{ left: `${el.x}%`, opacity: 0.7 }} />
                          <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                            style={{ top: `${el.y}%`, opacity: 0.7 }} />
                        </>
                      );
                    })()}
                    {dragTarget?.type === 'logo' && logoPosition && (
                      <>
                        <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                          style={{ left: `${logoPosition.x}%`, opacity: 0.7 }} />
                        <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                          style={{ top: `${logoPosition.y}%`, opacity: 0.7 }} />
                      </>
                    )}
                    {dragTarget?.type === 'qr' && backQrPosition && (
                      <>
                        <div className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none z-30"
                          style={{ left: `${backQrPosition.x}%`, opacity: 0.7 }} />
                        <div className="absolute left-0 right-0 h-px bg-cyan-400 pointer-events-none z-30"
                          style={{ top: `${backQrPosition.y}%`, opacity: 0.7 }} />
                      </>
                    )}
                  </>
                )}
                {logoPosition && (
                <div className="absolute flex items-center justify-center cursor-grab active:cursor-grabbing overflow-hidden"
                  style={{
                    left: `${logoPosition.x}%`, top: `${logoPosition.y}%`,
                    width: `${logoPosition.size}%`,
                    ...(logoPosition.shape === 'rounded-rect' ? { aspectRatio: '1.4/1' } : { aspectRatio: '1' }),
                    transform: 'translate(-50%, -50%)',
                    border: logoPosition.image ? 'none' : '2px dashed rgba(212,175,55,0.5)',
                    ...getLogoShapeStyle(logoPosition.shape || 'circle'),
                    backgroundColor: logoPosition.image ? 'transparent' : 'rgba(212,175,55,0.08)',
                    touchAction: 'none', // Prevent touch scrolling
                  }}
                  onMouseDown={(e) => handleCanvasMouseDown(e, 'logo', 'logo')}
                  onTouchStart={(e) => handleCanvasMouseDown(e, 'logo', 'logo')}
                  onClick={(e) => { e.stopPropagation(); setActivePanel('logo'); }}
                  data-testid="logo-placeholder"
                >
                  {logoPosition.image ? (
                    <img src={logoPosition.image} alt="Logo" className="w-full h-full object-cover" draggable={false} style={getLogoShapeStyle(logoPosition.shape || 'circle')} />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Upload className="w-4 h-4 text-amber-500/60" />
                      <span className="text-[10px] font-medium text-amber-500/60 uppercase tracking-wider">Logo</span>
                    </div>
                  )}
                </div>
                )}

                {textElements.map(el => {
                  const alignment = el.textAlign || 'center';
                  const transformX = alignment === 'left' ? '0%' : alignment === 'right' ? '-100%' : '-50%';
                  return (
                  <div key={el.id}
                    className={`absolute cursor-grab active:cursor-grabbing transition-shadow ${selectedElement === el.id ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                    style={{
                      left: `${el.x}%`, top: `${el.y}%`,
                      transform: `translate(${transformX}, -50%)`,
                      fontSize: `${el.fontSize}pt`, fontFamily: el.fontFamily,
                      fontWeight: el.fontWeight, color: el.color,
                      textAlign: alignment, whiteSpace: 'nowrap',
                      userSelect: 'none', padding: '0',
                      zIndex: editingTextId === el.id ? 20 : 1,
                      touchAction: 'none', // Prevent touch scrolling
                    }}
                    onMouseDown={(e) => {
                      if (editingTextId !== el.id) {
                        handleCanvasMouseDown(e, el.id, 'text');
                      }
                    }}
                    onTouchStart={(e) => {
                      if (editingTextId !== el.id) {
                        handleCanvasMouseDown(e, el.id, 'text');
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedElement(el.id);
                      setActivePanel('text');
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingTextId(el.id);
                      setSelectedElement(el.id);
                      setActivePanel('text');
                    }}
                    data-testid={`canvas-text-${el.id}`}
                  >
                    {editingTextId === el.id ? (
                      <div style={{
                        display: 'flex',
                        justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
                      }}>
                        <input
                          autoFocus
                          value={el.text}
                          style={{
                            fontSize: `${el.fontSize}pt`, 
                            fontFamily: el.fontFamily, 
                            fontWeight: el.fontWeight,
                            color: el.color, 
                            textAlign: alignment,
                            width: 'auto',
                            minWidth: '50px',
                            maxWidth: '500px',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: '0',
                            margin: '0',
                            caretColor: el.color || '#FFFFFF',
                            direction: alignment === 'right' ? 'rtl' : 'ltr',
                          }}
                          onChange={(e) => {
                          updateTextElement(el.id, 'text', e.target.value);
                        }}
                        onBlur={() => {
                          setEditingTextId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.target.blur();
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditingTextId(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        data-testid={`canvas-edit-input-${el.id}`}
                      />
                      </div>
                    ) : el.text}
                  </div>
                );})}

                {activeSide === 'back' && backQrPosition && (() => {
                  const qrColor = backQrPosition.color || '#ffffff';
                  return (
                  <div className="absolute cursor-grab active:cursor-grabbing flex flex-col items-center justify-center"
                    style={{
                      left: `${backQrPosition.x}%`, top: `${backQrPosition.y}%`,
                      width: `${backQrPosition.size}%`, aspectRatio: '1',
                      transform: 'translate(-50%, -50%)',
                      border: `2px dashed ${qrColor}80`,
                      borderRadius: '8px',
                      backgroundColor: `${qrColor}14`,
                      touchAction: 'none', // Prevent touch scrolling
                    }}
                    onMouseDown={(e) => handleCanvasMouseDown(e, 'qr', 'qr')}
                    onTouchStart={(e) => handleCanvasMouseDown(e, 'qr', 'qr')}
                    onClick={(e) => { e.stopPropagation(); setActivePanel('qr'); }}
                    data-testid="qr-placeholder"
                  >
                    <svg viewBox="0 0 24 24" className="w-1/2 h-1/2" fill="none" stroke={qrColor} strokeWidth="1.5" style={{ opacity: 0.6 }}>
                      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /><rect x="18" y="18" width="3" height="3" />
                    </svg>
                    <span className="text-[9px] mt-0.5" style={{ color: `${qrColor}99` }}>pipercard.com</span>
                  </div>
                  );
                })()}

                {alignGuides.map((guide, i) => (
                  guide.type === 'vertical' ? (
                    <div key={`g-${i}`} className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${guide.pos}%`, width: '1px', background: '#ef4444', zIndex: 50 }} />
                  ) : (
                    <div key={`g-${i}`} className="absolute left-0 right-0 pointer-events-none" style={{ top: `${guide.pos}%`, height: '1px', background: '#ef4444', zIndex: 50 }} />
                  )
                ))}
              </div>
              </div>{/* end canvas scaling wrapper */}
              <p className="text-[10px] text-gray-400 mt-1.5 text-center">Drag elements to reposition</p>
              
              {/* Thumbnail Image Upload - Below Design */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Label className="text-xs font-medium text-gray-700">Thumbnail Image (for category page)</Label>
                <p className="text-[10px] text-gray-500 mb-2">Upload a custom image to show in shop/category instead of design preview</p>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  ref={thumbnailInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setThumbnailImage(ev.target.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {thumbnailImage ? (
                  <div className="relative">
                    <img src={thumbnailImage} alt="Thumbnail" className="w-full h-32 object-cover rounded-lg border" />
                    <button 
                      onClick={() => setThumbnailImage(null)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-xs border-dashed border-2"
                    onClick={() => thumbnailInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" /> Upload Thumbnail Image
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Template Info sidebar */}
          <div className="lg:w-72 flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l overflow-y-auto order-3" data-testid="template-info-panel">
            <div className="p-3 space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">Template Info</h3>
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" className="h-8 text-sm" data-testid="builder-name-input" />
              </div>
              <div>
                <Label className="text-xs">Category *</Label>
                <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setSubcategoryId(''); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="builder-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">MRP (₹)</Label>
                  <Input type="number" value={templateMrp} onChange={(e) => setTemplateMrp(e.target.value)} placeholder="999" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Selling Price (₹) *</Label>
                  <Input type="number" value={templatePrice} onChange={(e) => setTemplatePrice(e.target.value)} placeholder="899" className="h-8 text-sm" data-testid="builder-price-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <div className="mt-1 border rounded-lg overflow-hidden">
                  <RichTextEditor value={templateDescription} onChange={setTemplateDescription} placeholder="Write description..." className="min-h-[60px]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminTemplateBuilder;
